import http from "../core/api/httpClient";
import {
  AssistantCaseListResponse,
  AssistantCaseCurrentStageDocumentsResponse,
  AssistantCaseListItem,
  AssistantCaseTaskListResponse,
  AssistantLeadListResponse,
  AssistantPaginationMeta,
  CaseStageTransitionPayload,
  CaseStageTransitionResponse,
  CaseDocumentReviewResponse,
  LeadCurrentStageDocumentsResponse,
  LeadDocumentReviewPayload,
  LeadDocumentReviewResponse,
  LeadDocumentUploadResponse,
  AssistantAssignedTaskItem,
  AssistantAssignedTaskListResponse,
  AssistantTaskSource,
  AssistantTaskStatus,
  AdvisorUser,
  AssistantUser,
  AssistantLeadTaskItem,
  AssistantLeadTaskListResponse,
  ConsultationBookingPayload,
  ConsultationBookingResponse,
  InitialLeadContactOutcomePayload,
  InitialLeadContactOutcomeResponse,
  LeadPhoneUpdatePayload,
  LeadPhoneUpdateResponse,
  CreateLeadPayload,
  CreateLeadResponse,
  EngagementLetterResponse,
  LeadConversionResponse,
  LeadListItem,
  ReminderRunResponse,
} from "../types/assistant.types";
import { WorkflowTemplateResponse } from "../types/workflowTemplate.types";

type LeadListPayload = LeadListItem[] | AssistantLeadListResponse;
type CaseListPayload = AssistantCaseListItem[] | AssistantCaseListResponse;

const normalizeOptional = (value?: string) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const extractLeadList = (payload: LeadListPayload): LeadListItem[] => {
  if (Array.isArray(payload)) return payload;
  return Array.isArray(payload.leads) ? payload.leads : [];
};

const extractCaseList = (payload: CaseListPayload): AssistantCaseListItem[] => {
  if (Array.isArray(payload)) return payload;
  return Array.isArray(payload.cases) ? payload.cases : [];
};

const toPagination = (
  pagination: AssistantPaginationMeta | undefined,
  params: {
    count: number;
    limit: number;
    offset: number;
  }
): AssistantPaginationMeta => {
  const { count, limit, offset } = params;
  if (pagination) {
    return {
      ...pagination,
      returned: count,
    };
  }
  return {
    total: count,
    limit,
    offset,
    returned: count,
    has_more: false,
  };
};

export const assistantService = {
  async getAssistant(assistantId: string): Promise<AssistantUser> {
    const res = await http.get<AssistantUser>(`/users/${assistantId}`);
    return res.data;
  },

  async listAdvisors(firmId: string): Promise<AdvisorUser[]> {
    const res = await http.get<AssistantUser[]>("/users", {
      params: {
        firm_id: firmId,
        role: "ADVISOR",
        is_active: true,
        limit: 200,
        offset: 0,
      },
    });

    return res.data.map((item) => ({
      id: item.id,
      first_name: item.first_name,
      last_name: item.last_name,
    }));
  },

  async listLeads(
    params?: { limit?: number; offset?: number }
  ): Promise<AssistantLeadListResponse> {
    const limit = params?.limit ?? 20;
    const offset = params?.offset ?? 0;
    const res = await http.get<LeadListPayload>(`/assistants/me/leads`, {
      params: { limit, offset },
    });
    const leads = extractLeadList(res.data);
    const payload = Array.isArray(res.data) ? undefined : res.data;
    return {
      assistant_id: payload?.assistant_id ?? "",
      leads,
      pagination: toPagination(payload?.pagination, {
        count: leads.length,
        limit,
        offset,
      }),
    };
  },

  async listCases(
    params?: { limit?: number; offset?: number }
  ): Promise<AssistantCaseListResponse> {
    const limit = params?.limit ?? 20;
    const offset = params?.offset ?? 0;
    const res = await http.get<CaseListPayload>(`/assistant/caseload`, {
      params: { limit, offset },
    });
    const cases = extractCaseList(res.data);
    const payload = Array.isArray(res.data) ? undefined : res.data;
    return {
      assistant_id: payload?.assistant_id ?? "",
      cases,
      pagination: toPagination(payload?.pagination, {
        count: cases.length,
        limit,
        offset,
      }),
    };
  },

  async getLeadTasks(
    leadId: string,
    params?: { status?: AssistantTaskStatus; limit?: number; offset?: number }
  ): Promise<AssistantLeadTaskListResponse> {
    const res = await http.get<AssistantLeadTaskListResponse>(`/leads/${leadId}/tasks`, {
      params: {
        status: params?.status,
        limit: params?.limit ?? 200,
        offset: params?.offset ?? 0,
      },
    });
    return res.data;
  },

  async getCaseTasks(
    caseId: string,
    params?: { status?: AssistantTaskStatus; limit?: number; offset?: number }
  ): Promise<AssistantCaseTaskListResponse> {
    const res = await http.get<AssistantCaseTaskListResponse>(`/cases/${caseId}/tasks`, {
      params: {
        status: params?.status,
        limit: params?.limit ?? 200,
        offset: params?.offset ?? 0,
      },
    });
    return res.data;
  },

  async getLeadCurrentStageDocuments(leadId: string): Promise<LeadCurrentStageDocumentsResponse> {
    const res = await http.get<LeadCurrentStageDocumentsResponse>(
      `/leads/${leadId}/documents/current-stage`
    );
    return res.data;
  },

  async getCaseCurrentStageDocuments(
    caseId: string
  ): Promise<AssistantCaseCurrentStageDocumentsResponse> {
    const res = await http.get<AssistantCaseCurrentStageDocumentsResponse>(
      `/cases/${caseId}/documents/current-stage`
    );
    return res.data;
  },

  async uploadLeadDocument(
    leadId: string,
    documentItemId: string,
    file: File,
    uploadedBy?: string
  ): Promise<LeadDocumentUploadResponse> {
    const form = new FormData();
    form.append("file", file);
    if (uploadedBy) {
      form.append("uploaded_by", uploadedBy);
    }
    const res = await http.post<LeadDocumentUploadResponse>(
      `/leads/${leadId}/documents/${documentItemId}/upload`,
      form
    );
    return res.data;
  },

  async reviewLeadDocument(
    leadId: string,
    documentItemId: string,
    payload: LeadDocumentReviewPayload
  ): Promise<LeadDocumentReviewResponse> {
    const res = await http.post<LeadDocumentReviewResponse>(
      `/leads/${leadId}/documents/${documentItemId}/review`,
      payload
    );
    return res.data;
  },

  async reviewCaseDocument(
    caseId: string,
    documentItemId: string,
    payload: LeadDocumentReviewPayload
  ): Promise<CaseDocumentReviewResponse> {
    const res = await http.post<CaseDocumentReviewResponse>(
      `/cases/${caseId}/documents/${documentItemId}/review`,
      payload
    );
    return res.data;
  },

  async getWorkflowTemplate(templateId: string): Promise<WorkflowTemplateResponse> {
    const res = await http.get<WorkflowTemplateResponse>(`/workflow_templates/${templateId}`);
    return res.data;
  },

  async listWorkflowTemplates(params: {
    firm_id: string;
    visa_type?: string;
    country_code?: string;
    is_active?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<WorkflowTemplateResponse[]> {
    const res = await http.get<WorkflowTemplateResponse[]>("/workflow_templates", {
      params: {
        firm_id: params.firm_id,
        visa_type: normalizeOptional(params.visa_type),
        country_code: normalizeOptional(params.country_code),
        is_active: params.is_active,
        limit: params.limit ?? 200,
        offset: params.offset ?? 0,
      },
    });
    return res.data;
  },

  async updateLeadTaskStatus(
    leadId: string,
    taskId: string,
    status: AssistantTaskStatus,
    completedBy?: string
  ): Promise<void> {
    await http.patch(`/leads/${leadId}/tasks/${taskId}`, {
      status,
      completed_by: completedBy,
    });
  },

  async updateCaseTaskStatus(
    caseId: string,
    taskId: string,
    status: AssistantTaskStatus,
    completedBy?: string
  ): Promise<void> {
    await http.patch(`/cases/${caseId}/tasks/${taskId}`, {
      status,
      completed_by: completedBy,
    });
  },

  async transitionCaseStage(
    caseId: string,
    payload: CaseStageTransitionPayload
  ): Promise<CaseStageTransitionResponse> {
    const res = await http.post<CaseStageTransitionResponse>(`/cases/${caseId}/stage`, payload);
    return res.data;
  },

  async listAssignedTasks(
    params?: {
      status?: AssistantTaskStatus;
      source?: AssistantTaskSource;
      limit?: number;
      offset?: number;
    }
  ): Promise<AssistantAssignedTaskListResponse> {
    const res = await http.get<AssistantAssignedTaskListResponse>("/users/me/tasks", {
      params: {
        status: params?.status,
        source: params?.source,
        limit: params?.limit ?? 200,
        offset: params?.offset ?? 0,
      },
    });
    return res.data;
  },

  async updateAssignedTaskStatus(
    task: AssistantAssignedTaskItem,
    status: AssistantTaskStatus,
    completedBy?: string
  ): Promise<void> {
    if (task.source === "CASE") {
      await this.updateCaseTaskStatus(task.source_id, task.task_id, status, completedBy);
      return;
    }
    await this.updateLeadTaskStatus(task.source_id, task.task_id, status, completedBy);
  },

  async runDueReminders(params: {
    firmId?: string;
    startedBy?: string;
    limit?: number;
    dryRun?: boolean;
    includeTypes?: string[];
  } = {}): Promise<ReminderRunResponse> {
    const payload = {
      firm_id: params.firmId ?? null,
      limit: params.limit ?? 200,
      dry_run: params.dryRun ?? false,
      include_types: params.includeTypes ?? ["document", "task"],
      started_by: params.startedBy ?? null,
    };
    const res = await http.post<ReminderRunResponse>("/internal/reminders/run-due", payload);
    return res.data;
  },

  async createLead(payload: CreateLeadPayload): Promise<CreateLeadResponse> {
    const res = await http.post<CreateLeadResponse>("/leads", {
      ...payload,
      phone: normalizeOptional(payload.phone),
      source_channel: normalizeOptional(payload.source_channel),
      enquiry_subject: normalizeOptional(payload.enquiry_subject),
      consultation_date: normalizeOptional(payload.consultation_date),
      notes: normalizeOptional(payload.notes),
      assigned_advisor_id: payload.assigned_advisor_id || undefined,
    });
    return res.data;
  },

  async bookConsultation(
    leadId: string,
    payload: ConsultationBookingPayload
  ): Promise<ConsultationBookingResponse> {
    const res = await http.post<ConsultationBookingResponse>(
      `/leads/${leadId}/consultation/book`,
      {
        consultation_date: payload.consultation_date,
        consultation_notes: normalizeOptional(payload.consultation_notes),
        qualification_outcome: normalizeOptional(payload.qualification_outcome),
      }
    );
    return res.data;
  },

  async updateLeadPhone(
    leadId: string,
    payload: LeadPhoneUpdatePayload
  ): Promise<LeadPhoneUpdateResponse> {
    const normalizedPhone = payload.phone.trim();
    const res = await http.patch<LeadPhoneUpdateResponse>(
      `/leads/${leadId}/contact/phone`,
      {
        phone: normalizedPhone,
      }
    );
    return res.data;
  },

  async recordInitialLeadContactOutcome(
    leadId: string,
    payload: InitialLeadContactOutcomePayload
  ): Promise<InitialLeadContactOutcomeResponse> {
    const normalizedPhone = payload.phone?.trim();
    const normalizedNotes = payload.notes?.trim();
    const res = await http.post<InitialLeadContactOutcomeResponse>(
      `/leads/${leadId}/contact/outcome`,
      {
        outcome: payload.outcome,
        phone: normalizedPhone || undefined,
        notes: normalizedNotes || undefined,
        call_logged_at: payload.call_logged_at,
        contacted_by: payload.contacted_by,
      }
    );
    return res.data;
  },

  async sendEngagementLetter(leadId: string): Promise<EngagementLetterResponse> {
    const res = await http.post<EngagementLetterResponse>(
      `/leads/${leadId}/engagement-letter/send`
    );
    return res.data;
  },

  async createEngagementSignedDocumentTask(leadId: string): Promise<AssistantLeadTaskItem> {
    const res = await http.post<AssistantLeadTaskItem>(
      `/leads/${leadId}/engagement/signed-document-task`
    );
    return res.data;
  },

  async convertLeadToClient(leadId: string): Promise<LeadConversionResponse> {
    const res = await http.post<LeadConversionResponse>(`/leads/${leadId}/convert-to-client`);
    return res.data;
  },
};
